import { useState } from 'react'
import { useTheme } from '../../theme'
import { useAuth } from '../../AuthContext'
import RinaContextCard from '../../components/RinaContextCard'
import { classeChamp } from './champs'
import { useLocation } from 'wouter'
import PageHub from '../../components/PageHub'
import { ROUTES } from '../../routes'
import { MVP } from '../../mvp'

/**
 * Sous-vue « Mon profil » du Compte (prototype v7.4, extraite en S54).
 * L'edition se fait EN LIGNE dans la carte — pas de sous-page « edit » : le
 * prototype garde l'utilisateur au meme endroit, avec Annuler / Enregistrer.
 * L'etat d'edition est LOCAL : entrer dans la vue la monte a neuf (toujours
 * en lecture), plus de reset manuel a l'ouverture.
 * `emailManquant` : email absent ou fictif (@phone.manrina.local) — bandeau
 * « Securisez votre compte » et redirections vers Securite & connexion.
 */
function ProfilContenu({ emailManquant, onOuvrirSecurite }) {
  const { isWeb3 } = useTheme()
  const { user, updateProfile } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = 'bg-card border-card-border'
  const inputCls = classeChamp(isWeb3)

  // Edition EN LIGNE : `__ouvert` marque le mode, le brouillon vit dans
  // `editData` et n'est ecrit qu'a l'enregistrement.
  const [editData, setEditData] = useState({})
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState('')
  const [editSuccess, setEditSuccess] = useState(false)
  const enEdition = !!editData.__ouvert

  const ouvrirEdition = () => {
    setEditError('')
    setEditSuccess(false)
    setEditData({
      __ouvert: true,
      prenom: user?.prenom || '',
      nom: user?.nom || '',
      ville: user?.commune || '',
    })
  }
  const annulerEdition = () => {
    setEditError('')
    setEditData({})
  }

  const handleSaveProfile = async () => {
    setEditError('')
    setEditLoading(true)
    try {
      await updateProfile({
        prenom: editData.prenom,
        nom: editData.nom,
        commune: editData.ville,
      })
      setEditSuccess(true)
      setEditData({})     // sortie du mode edition, on reste sur la page
      setTimeout(() => setEditSuccess(false), 3000)
    } catch (err) {
      setEditError(err.message || 'Erreur lors de la sauvegarde')
    } finally {
      setEditLoading(false)
    }
  }

  const lignes = [
    { i: '🧑🏾', l: 'Nom', v: `${user?.prenom || ''} ${user?.nom || ''}`.trim() || 'Non renseigné' },
    { i: '✉️', l: 'Email', v: emailManquant ? 'Non renseigné' : user.email, terne: emailManquant },
    { i: '📱', l: 'Téléphone', v: user?.telephone || 'Non renseigné', terne: !user?.telephone },
    { i: '📍', l: 'Commune', v: user?.commune || 'Non renseignée', terne: !user?.commune },
  ]
  const champs = [
    { k: 'prenom', l: 'Prénom', ph: 'Votre prénom' },
    { k: 'nom', l: 'Nom', ph: 'Votre nom' },
    { k: 'ville', l: 'Commune', ph: 'Votre commune' },
  ]

  return (
    <div className="animate-fade-up">
      <RinaContextCard
        title="Mon profil"
        message={emailManquant
          ? "Pensez à ajouter votre email — il permet de récupérer votre code PIN en cas d'oubli."
          : 'Vos informations personnelles, en un seul endroit.'}
      />

      <div className={`rounded-3xl border p-5 ${cardBg}`}>
        <div className="flex justify-between items-center mb-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-teal">Mes informations</p>
          {!enEdition && (
            <button onClick={ouvrirEdition} className="text-[12.5px] font-bold" style={{ color: 'rgb(var(--c-primary))' }}>
              ✏️ Modifier
            </button>
          )}
        </div>

        {enEdition ? (
          <>
            {champs.map((c) => (
              <div key={c.k} className="py-2">
                <label htmlFor={`p-${c.k}`} className={`block text-[11px] font-semibold mb-1.5 ${ts}`}>{c.l}</label>
                <input id={`p-${c.k}`} type="text" value={editData[c.k] || ''} placeholder={c.ph}
                  onChange={(e) => setEditData((d) => ({ ...d, [c.k]: e.target.value }))}
                  className={inputCls} disabled={editLoading} />
              </div>
            ))}

            {/* Email et telephone ne s'editent pas ici : ce sont des METHODES DE
                CONNEXION (elles changent la facon de se connecter, avec
                verification). Elles se gerent dans « Sécurité & connexion ». */}
            <p className={`text-[11px] leading-relaxed mt-1 ${ts}`}>
              Votre email et votre téléphone se modifient depuis
              <button onClick={onOuvrirSecurite} className="font-bold" style={{ color: 'rgb(var(--c-primary))' }}> Sécurité & connexion ›</button>
            </p>

            {editError && <p className="text-[12px] text-red-500 mt-2">{editError}</p>}

            <div className="flex gap-2.5 mt-3.5">
              <button onClick={annulerEdition} disabled={editLoading}
                className={`flex-1 py-3 rounded-[15px] border text-[13.5px] font-bold ${ts}`}
                style={{ borderColor: 'rgba(7,62,61,0.14)', background: 'transparent' }}>
                Annuler
              </button>
              <button onClick={handleSaveProfile} disabled={editLoading}
                className="flex-[2] py-3 rounded-[15px] text-white text-[13.5px] font-extrabold disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), #e0653a)' }}>
                {editLoading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </>
        ) : (
          lignes.map((f, i) => (
            <div key={f.l} className={`flex items-center gap-3 py-3 ${i > 0 ? `border-t ${isWeb3 ? 'border-white/5' : 'border-ink/[0.07]'}` : ''}`}>
              <span className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center text-[17px]"
                style={{ background: 'rgba(20,184,166,0.1)', border: '1px solid rgba(255,255,255,0.7)' }}>{f.i}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-semibold ${ts}`}>{f.l}</p>
                <p className={`text-[15px] font-bold truncate ${f.terne ? ts : tc}`}>{f.v}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {editSuccess && (
        <div className={`p-3 rounded-xl mt-3.5 text-[12px] font-medium flex items-center gap-2 animate-fade-up ${
          isWeb3 ? 'bg-teal/20 text-[#5eead4]' : 'bg-teal/10 text-[#0a6b62]'
        }`}>
          <span className="badge-check" style={{ width: 20, height: 20, fontSize: 11 }}>✓</span> Profil mis à jour
        </div>
      )}

      {/* Bandeau « Securisez votre compte » — remis par la v7.4 sur CETTE page,
          la ou l'on peut agir. Il avait ete retire en S31. */}
      {emailManquant && !enEdition && (
        <div className={`rounded-[22px] border p-4 mt-3.5 animate-fade-up ${cardBg}`}
          style={{ background: 'linear-gradient(120deg, rgba(255,255,255,0.6), rgba(244,137,83,0.12))' }}>
          <div className="flex gap-3 items-start">
            <span className="w-10 h-10 rounded-[13px] flex-shrink-0 flex items-center justify-center text-[19px]"
              style={{ background: 'rgba(244,137,83,0.16)', border: '1px solid rgba(255,255,255,0.8)' }}>🛡️</span>
            <div className="flex-1 min-w-0">
              <p className={`text-[14.5px] font-bold ${tc}`}>Sécurisez votre compte</p>
              <p className={`text-[12px] leading-snug mt-0.5 ${ts}`}>
                Ajoutez votre email pour récupérer votre code PIN en cas d’oubli.
              </p>
            </div>
          </div>
          <button onClick={onOuvrirSecurite}
            className="mt-3.5 w-full py-3 rounded-[14px] text-white text-sm font-extrabold"
            style={{ background: 'linear-gradient(120deg, rgba(244,137,83,0.95), rgba(224,101,58,0.95))' }}>
            + Ajouter mon email
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Page « Mon profil » (S56) : une vraie Route (/account/profil).
 */
export default function PageProfil() {
  const [, setLocation] = useLocation()
  const { user } = useAuth()
  // MVP (S62) : pas de relance email tant que MVP.EMAIL_REQUIS est faux
  const emailManquant = MVP.EMAIL_REQUIS && (!user?.email || user.email.endsWith('@phone.manrina.local'))
  return (
    <PageHub retour={{ label: 'Compte', vers: ROUTES.compte.base }}>
      <ProfilContenu emailManquant={emailManquant} onOuvrirSecurite={() => setLocation(ROUTES.compte.securite)} />
    </PageHub>
  )
}
